// src/features/data/avatars-cell.tsx
// IR-1147 reopen (Tomer 2026-06-30) — Avatars cell for the Data Management
// Groups table. The device name(s) stay visible inline (Rotem: "I want to see
// the assigned avatar"); hovering reveals each connected avatar's device name +
// mobile number, mirroring the Join Links tab's SourceGroupsCell tooltip that
// Tomer cited. Groups with no avatar mapping render an em-dash (no tooltip).
//
// IR-1426 r2 (Rotem 2026-08-24) — she asked: "is the Avatar column reflecting
// the reporting or the status reported by the platform? if it reflects the
// excel add indication on the group 'Reported'".
//
// It reflects the ASSIGNMENT — the Excel seed plus the automatic matcher —
// never the phone. So the indication she asked for is added here, and it is a
// mark on the GROUP (her words: "indication on the group"), driven by the
// row-level `on_phone` fold rather than by any one avatar: 19% of groups are
// covered by two or more avatars, and a group IS on a phone the moment one
// coverer reports it. The per-avatar breakdown is not thrown away — it moves
// into the tooltip, which is where a disagreement between two coverers can be
// read without crowding the cell.
//
// IR-1426 r3 (Rotem, 2026-09-22 consolidation: "retain them under the avatar
// that actually covers them"; IR-1703: a group listed under S7_2 AND S8_2 while
// only S8_2's phone has it) — the names shown INLINE are now the avatars whose
// phone currently lists the group. An avatar whose phone reported and omitted
// it (`on_phone === false`) moves into the tooltip, labelled by the shared
// mark, so the old link is still findable without being presented as current
// coverage. Two deliberate edges: an avatar that has never reported stays
// inline (silence is not evidence — IR-1228 P4), and when EVERY coverer is
// stale nothing is hidden, because an empty cell would read as "no avatar at
// all" when the truth is "assigned, and no phone confirms it" (the Coverage
// column beside it says Stopped Reporting). Nothing here deletes or
// unassigns — it is attribution on screen only.
import { StatusPill } from "@/components/iris";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { groupPhoneMark } from "./avatars-tab.helpers";
import { fmtAvatarNames, fmtAvatarPhone } from "./groups-unified-tab.helpers";

export interface AvatarRef {
  avatar_name: string;
  avatar_id: number;
  platform: string;
  phone_number: string | null;
  /** IR-1426: did THIS avatar's phone list this group in its latest roster? */
  on_phone?: boolean | null | undefined;
}

/** IR-1426 r3: the avatars to show inline — current coverers (plus the
 *  never-reported ones), falling back to everyone when no coverer is current. */
export function inlineAvatars<T extends { on_phone?: boolean | null | undefined }>(
  avatars: T[],
): T[] {
  const covering = avatars.filter((a) => a.on_phone !== false);
  return covering.length > 0 ? covering : avatars;
}

export function AvatarsCell({
  avatars,
  onPhone,
}: {
  avatars: AvatarRef[];
  /** IR-1426 r2: the row-level fold (`GroupRow.on_phone`). Tri-state — absent
   *  and null both mean "no covering avatar has ever reported", which is every
   *  WhatsApp group until IR-1433 lands. */
  onPhone?: boolean | null | undefined;
}) {
  if (!avatars || avatars.length === 0) {
    return <span style={{ color: "var(--iris-fg-4)" }}>—</span>;
  }

  const shown = inlineAvatars(avatars);
  const hidden = avatars.length - shown.length;
  const plural = shown.length > 1 ? "s" : "";
  // Same helper the Avatars-tab expander uses, so the two surfaces cannot drift
  // apart on either the wording or the tri-state contract.
  const mark = groupPhoneMark({ on_phone: onPhone });

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`Show device name, mobile number and phone-reporting state for ${shown.length} connected avatar${plural}${hidden > 0 ? ` (${hidden} more no longer on a phone)` : ""}`}
            data-testid="avatars-cell-trigger"
            data-hidden-count={hidden}
            style={{
              color: "var(--iris-fg-3)",
              whiteSpace: "nowrap",
              background: "none",
              border: "none",
              padding: 0,
              margin: 0,
              font: "inherit",
              cursor: "help",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span data-testid="avatars-cell-inline">{fmtAvatarNames(shown)}</span>
            {/* Unknown stays bare. Rendering "Not reported" over silence would
                accuse every WhatsApp group on the tab of being a stale Excel
                row on the strength of an answer we never received. */}
            {mark.tone !== "unknown" && (
              <span data-testid="group-reported-mark">
                <StatusPill variant={mark.tone === "on" ? "ok" : "inactive"}>
                  {mark.label}
                </StatusPill>
              </span>
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[280px]">
          <ul className="flex flex-col gap-1.5">
            {avatars.map((a) => {
              const per = groupPhoneMark(a);
              return (
                <li
                  key={a.avatar_id}
                  className="flex flex-col"
                  data-testid="avatars-cell-tooltip-row"
                  data-on-phone={String(a.on_phone ?? "unknown")}
                >
                  <span className="font-medium text-[var(--iris-fg-1)]">{a.avatar_name}</span>
                  <span className="text-[var(--iris-fg-3)]">{fmtAvatarPhone(a.phone_number)}</span>
                  {/* Per-avatar, because the row fold deliberately hides a
                      disagreement: one coverer reporting the group and another
                      not is exactly the case an operator needs to see. */}
                  <span className="text-[var(--iris-fg-3)]">
                    {per.tone === "unknown" ? "Never reported its group list" : per.label}
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="mt-2 text-[var(--iris-fg-3)]">{mark.detail}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
